import React, { useState } from 'react';
import { motion } from 'motion/react';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import { 
  Scissors, 
  Users, 
  History, 
  Bell, 
  Star, 
  Clock, 
  DollarSign, 
  LogOut, 
  Settings, 
  Calendar, 
  UserCircle, 
  ChevronRight, 
  CheckCircle2 
} from 'lucide-react';
import { MobileBottomNav } from '../../ui/MobileBottomNav';
import { StatusBadge } from '../../ui/StatusBadge';
import { ServiceManagement } from './ServiceManagement';
import { cn } from '../../../lib/utils';
import { UserProfile, Service } from '../../../types';

interface BarberDashboardProps {
  user: UserProfile;
  bookings: any[];
  onUpdateStatus: (id: string, status: string) => void;
  onUpdateBarberStatus: (status: string) => void;
  onChat: (id: string) => void;
  onEditProfile: () => void;
  onUpdateProfile: (data: any) => void;
  onLogOut: () => void;
  onHome: () => void;
  unreadCount?: number;
  msgUnreadMap?: Record<string, number>;
  systemServices?: Service[];
}

export const BarberDashboard = ({ 
  user, 
  bookings, 
  onUpdateStatus, 
  onUpdateBarberStatus, 
  onChat, 
  onEditProfile, 
  onUpdateProfile, 
  onLogOut, 
  onHome, 
  unreadCount = 0, 
  msgUnreadMap = {},
  systemServices = []
}: BarberDashboardProps) => {
  const [activeTab, setActiveTab] = useState<'queue' | 'history' | 'messages' | 'services'>('queue');
  
  const filteredBookings = bookings.filter((b: any) => 
    activeTab === 'queue' ? (b.status === 'Menunggu' || b.status === 'Proses') : b.status === 'Selesai'
  );

  const dashboardTabs = [
    { id: 'queue', label: 'Antrean', icon: Users },
    { id: 'history', label: 'Arsip', icon: History },
    { id: 'services', label: 'Layanan', icon: Scissors },
    { id: 'messages', label: 'Notifikasi', icon: Bell, badge: unreadCount },
  ];

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-slate-50/50 pb-24 md:pb-0">
      <MobileBottomNav 
        tabs={dashboardTabs as any} 
        activeTab={activeTab} 
        onChange={setActiveTab as any} 
        user={user}
      />
      {/* Sidebar Navigation */}
      <aside className="hidden md:flex w-80 bg-white border-r border-slate-100 flex-col h-screen sticky top-0 z-50">
        <div className="p-8 border-b border-slate-50">
          <div className="flex items-center gap-4 mb-8 cursor-pointer group" onClick={onHome}>
            <div className="w-12 h-12 bg-slate-900 rounded-2xl flex items-center justify-center text-white shadow-xl shadow-slate-900/20 rotate-3 group-hover:rotate-12 transition-transform">
              <Scissors className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-black text-slate-900 tracking-tighter leading-none text-wrap group-hover:text-emerald-500 transition-colors">CukurJe <span className="text-emerald-500">Suite</span></h1>
              <p className="text-[9px] text-slate-400 font-black uppercase tracking-[0.2em] mt-1 group-hover:text-slate-600">Kembali ke Beranda</p>
            </div>
          </div>

          <div className="space-y-2">
            {dashboardTabs.map((tab: any) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={cn(
                  "w-full flex items-center gap-4 px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 group",
                  activeTab === tab.id 
                    ? "bg-slate-900 text-white shadow-2xl shadow-slate-900/40 translate-x-1" 
                    : "text-slate-400 hover:text-slate-900 hover:bg-slate-50"
                  )}
              >
                <div className={cn(
                  "p-2 rounded-xl transition-colors",
                  activeTab === tab.id ? "bg-white/10" : "bg-slate-50 group-hover:bg-white"
                )}>
                  <tab.icon className="w-4 h-4" />
                </div>
                {tab.label}
                {tab.badge > 0 && (
                  <span className="ml-auto w-5 h-5 bg-red-500 text-white text-[9px] font-black rounded-full flex items-center justify-center shadow-lg shadow-red-500/20 animate-bounce">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="p-8 mt-auto border-t border-slate-50 space-y-4">
          <div className="bg-slate-50 rounded-[2rem] p-6 border border-slate-100">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Status Kapster</p>
              <div className="flex items-center gap-1">
                <div className={cn(
                  "w-2 h-2 rounded-full",
                  user.status === 'Tersedia' ? "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]" : 
                  user.status === 'Sibuk' ? "bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]" : "bg-slate-400"
                )} />
                <span className="text-[10px] font-black text-slate-900 uppercase">{user.status || 'Tersedia'}</span>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-1.5 mb-6">
              {[
                { id: 'Tersedia', color: 'hover:border-emerald-500 text-emerald-500 bg-emerald-50/50' },
                { id: 'Sibuk', color: 'hover:border-amber-500 text-amber-500 bg-amber-50/50' },
                { id: 'Non-Aktif', color: 'hover:border-slate-500 text-slate-500 bg-slate-100' }
              ].map(s => (
                <button
                  key={s.id}
                  onClick={() => onUpdateBarberStatus(s.id)}
                  className={cn(
                    "py-2 rounded-xl border text-[8px] font-black uppercase transition-all",
                    user.status === s.id ? "bg-slate-900 text-white border-slate-900 shadow-lg" : "bg-white border-slate-100 text-slate-400 " + s.color
                  )}
                >
                  {s.id.split('-')[0]}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-4 mb-6 pt-4 border-t border-slate-100">
              <div className="relative">
                <img src={user.avatar} className="w-12 h-12 rounded-2xl object-cover shadow-lg" alt="" />
                <div className={cn(
                  "absolute -bottom-1 -right-1 w-4 h-4 border-4 border-slate-50 rounded-full",
                  user.status === 'Tersedia' ? "bg-emerald-500" : user.status === 'Sibuk' ? "bg-amber-500" : "bg-slate-400"
                )} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-black text-slate-900 truncate tracking-tight">{user.name}</p>
                <p className="text-[10px] text-emerald-600 font-black uppercase tracking-widest flex items-center gap-1">
                  <DollarSign className="w-2.5 h-2.5" />
                  Rp {user.price?.toLocaleString() || '0'}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button 
                onClick={onEditProfile}
                className="flex flex-col items-center justify-center gap-1.5 py-4 bg-white border border-slate-100 rounded-xl text-slate-400 hover:text-slate-900 hover:border-slate-300 transition-all group"
              >
                <Settings className="w-4 h-4 group-hover:rotate-90 transition-transform" />
                <span className="text-[8px] font-black uppercase">Edit</span>
              </button>
              <button 
                onClick={onLogOut}
                className="flex flex-col items-center justify-center gap-1.5 py-4 bg-white border border-slate-100 rounded-xl text-slate-400 hover:text-red-500 hover:border-red-100 transition-all"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-[8px] font-black uppercase">Keluar</span>
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Top Header */}
      <div className="md:hidden px-6 py-6 border-b border-slate-100 bg-white sticky top-0 z-40">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3" onClick={onHome}>
            <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-white">
              <Scissors className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-lg font-black text-slate-900 tracking-tighter leading-none">CukurJe</h1>
              <p className="text-[9px] text-emerald-500 font-black uppercase tracking-widest">Suite</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-[10px] font-black text-slate-900 leading-none mb-1">Rp {user.price?.toLocaleString() || '0'}</p>
              <p className="text-[8px] text-slate-400 font-bold uppercase tracking-widest leading-none">Harga Saya</p>
            </div>
            <button onClick={onEditProfile} className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center">
              <Settings className="w-5 h-5 text-slate-400" />
            </button>
            <button onClick={onLogOut} className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center">
              <LogOut className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>
        
        {/* Mobile Status Selector */}
        <div className="grid grid-cols-3 gap-2">
          {[
            { id: 'Tersedia', label: 'Tersedia', color: 'emerald' },
            { id: 'Sibuk', label: 'Sibuk', color: 'amber' },
            { id: 'Non-Aktif', label: 'Off', color: 'slate' }
          ].map(s => (
            <button
              key={s.id}
              onClick={() => onUpdateBarberStatus(s.id)}
              className={cn(
                "flex items-center justify-center gap-2 py-2.5 rounded-xl text-[10px] font-black uppercase transition-all border",
                user.status === s.id 
                  ? "bg-slate-900 text-white border-slate-900 shadow-lg scale-[1.02]" 
                  : "bg-white border-slate-100 text-slate-400"
              )}
            >
              <div className={cn(
                "w-1.5 h-1.5 rounded-full",
                user.status === s.id ? "bg-white" : 
                s.color === 'emerald' ? "bg-emerald-500" :
                s.color === 'amber' ? "bg-amber-500" : "bg-slate-400"
              )} />
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 px-4 md:px-12 py-4 md:py-12 overflow-y-auto">
        <div className="bg-white rounded-[2rem] md:rounded-[2.5rem] shadow-2xl shadow-slate-200/40 border border-slate-100 overflow-hidden flex flex-col min-h-[500px] md:min-h-[600px]">
          {/* Dashboard Content Header */}
          <div className="px-4 md:px-8 py-6 md:py-8 border-b border-slate-50 bg-slate-50/30">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="px-2 md:px-0 flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-2.5 h-2.5 rounded-full animate-pulse shadow-[0_0_10px_rgba(0,0,0,0.1)]",
                    user.status === 'Tersedia' ? "bg-emerald-500 shadow-emerald-500/50" : 
                    user.status === 'Sibuk' ? "bg-amber-500 shadow-amber-500/50" : "bg-slate-400 shadow-slate-400/50"
                  )} />
                  <h2 className="text-xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                    {activeTab === 'queue' ? 'Operasi Antrean' : activeTab === 'history' ? 'Riwayat Layanan' : 'Pusat Pesan'}
                    <span className="text-slate-300 font-thin">|</span>
                    <span className={cn(
                      "text-[10px] px-2 py-0.5 rounded-md border font-black uppercase tracking-widest",
                      user.status === 'Tersedia' ? "text-emerald-500 border-emerald-100 bg-emerald-50/50" :
                      user.status === 'Sibuk' ? "text-amber-500 border-amber-100 bg-amber-50/50" :
                      "text-slate-400 border-slate-100 bg-slate-50"
                    )}>
                      {user.status || 'Tersedia'}
                    </span>
                  </h2>
                </div>
              </div>

              <div className="flex items-center bg-white p-1 rounded-2xl shadow-sm border border-slate-100">
                <button 
                  onClick={() => setActiveTab('queue')}
                  className={cn(
                    "px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                    activeTab === 'queue' ? "bg-slate-900 text-white shadow-lg" : "text-slate-400 hover:text-slate-600"
                  )}
                >
                  Aktif
                </button>
                <button 
                  onClick={() => setActiveTab('history')}
                  className={cn(
                    "px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                    activeTab === 'history' ? "bg-slate-900 text-white shadow-lg" : "text-slate-400 hover:text-slate-600"
                  )}
                >
                  Selesai
                </button>
                <button 
                  onClick={() => setActiveTab('messages')}
                  className={cn(
                    "px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all relative flex items-center gap-2",
                    activeTab === 'messages' ? "bg-slate-900 text-white shadow-lg" : "text-slate-400 hover:text-slate-600"
                  )}
                >
                  Pesan
                  {unreadCount > 0 && activeTab !== 'messages' && (
                    <span className="w-4 h-4 bg-red-500 text-white text-[8px] rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </button>
                <button 
                  onClick={() => setActiveTab('services')}
                  className={cn(
                    "px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                    activeTab === 'services' ? "bg-slate-900 text-white shadow-lg" : "text-slate-400 hover:text-slate-600"
                  )}
                >
                  Layanan
                </button>
              </div>
            </div>
          </div>

          <div className="p-4 md:p-8">
            <div className="grid grid-cols-1 gap-6">
              {activeTab === 'services' ? (
                <ServiceManagement 
                  user={user} 
                  onUpdate={(services: any) => onUpdateProfile({ services })} 
                  systemServices={systemServices}
                />
              ) : activeTab === 'messages' ? (
                bookings.filter((b: any) => b.status !== 'Batal').length === 0 ? (
                  <div className="py-24 text-center">
                    <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Bell className="w-10 h-10 text-slate-200" />
                    </div>
                    <p className="text-slate-900 font-black text-xl mb-1 tracking-tight">Belum Ada Chat</p>
                    <p className="text-slate-400 font-bold uppercase text-[9px] tracking-widest">Percakapan dengan customer akan muncul di sini.</p>
                  </div>
                ) : (
                  bookings.filter((b: any) => b.status !== 'Batal').map((booking: any, index: number) => (
                    <motion.div 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      key={booking.id}
                      onClick={() => onChat(booking.id)}
                      className="group bg-white rounded-3xl border border-slate-100 p-6 flex items-center gap-6 cursor-pointer hover:shadow-xl hover:border-emerald-100 transition-all"
                    >
                      <div className="relative">
                        <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-400 group-hover:bg-emerald-50 group-hover:text-emerald-500 transition-colors">
                          <UserCircle className="w-8 h-8" />
                        </div>
                        {msgUnreadMap[booking.id] > 0 && (
                          <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center border-4 border-white animate-pulse">
                            {msgUnreadMap[booking.id]}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-black text-slate-900 truncate tracking-tight">{booking.customerName || 'Customer'}</h3>
                          <span className="text-[9px] font-bold text-slate-400 uppercase">{booking.time} • {booking.date}</span>
                        </div>
                        <p className="text-xs text-slate-500 truncate font-medium">Layanan: <span className="text-slate-900 font-bold">{booking.serviceName}</span></p>
                        <p className="text-[10px] text-emerald-500 font-black uppercase tracking-widest mt-1">Klik untuk membalas</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-emerald-500 transition-colors" />
                    </motion.div>
                  ))
                )
              ) : filteredBookings.length === 0 ? (
                <div className="py-24 text-center">
                  <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Calendar className="w-10 h-10 text-slate-200" />
                  </div>
                  <p className="text-slate-900 font-black text-xl mb-1 tracking-tight">
                    {activeTab === 'queue' ? 'Menunggu Order...' : 'Log Masih Kosong'}
                  </p>
                  <p className="text-slate-400 font-bold uppercase text-[9px] tracking-widest">
                    {activeTab === 'queue' ? 'Belum ada antrean masuk saat ini.' : 'Belum ada data layanan yang tersimpan.'}
                  </p>
                </div>
              ) : (
                filteredBookings.map((booking: any, index: number) => (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    key={booking.id} 
                    className="group bg-white rounded-3xl border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col md:flex-row relative"
                  >
                    <div className="absolute top-0 left-0 w-1.5 h-full" 
                         style={{ backgroundColor: booking.status === 'Proses' ? '#3b82f6' : booking.status === 'Selesai' ? '#10b981' : '#cbd5e1' }} />
                    
                    <div className="p-6 md:p-8 flex-1 flex flex-col md:flex-row gap-6 items-center">
                      <div className="flex items-center gap-6 w-full md:w-auto">
                        <div className={cn(
                          "w-16 h-16 rounded-2xl flex items-center justify-center shrink-0 shadow-sm",
                          booking.status === 'Selesai' ? "bg-emerald-500 text-white" : 
                          booking.status === 'Proses' ? "bg-blue-600 text-white animate-pulse" : "bg-slate-50 text-slate-300"
                        )}>
                          {booking.status === 'Selesai' ? <CheckCircle2 className="w-8 h-8" /> : <Clock className="w-8 h-8" />}
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <span className="text-3xl font-black text-slate-900 tracking-tighter tabular-nums leading-none">{booking.time}</span>
                            <StatusBadge status={booking.status} />
                          </div>
                          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                            <Calendar className="w-3 h-3" />
                            {format(new Date(booking.date), 'EEEE, d MMMM', { locale: id })}
                          </p>
                        </div>
                      </div>

                      <div className="hidden lg:block w-px h-12 bg-slate-100" />

                      <div className="flex-1 w-full sm:w-auto">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                          <div>
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Customer</p>
                            <h3 className="text-lg font-black text-slate-900 tracking-tight">{booking.customerName || 'Klien Baru'}</h3>
                          </div>
                          <div className="text-left sm:text-right">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{booking.serviceName}</p>
                            <p className="text-xl font-black text-slate-900 tracking-tighter">Rp {booking.totalPrice.toLocaleString()}</p>
                          </div>
                        </div>

                        {/* Payment Info Sub-Panel */}
                        <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2.5 text-[10px]">
                          <div className="flex items-center gap-2">
                            <span className="font-extrabold text-slate-400 uppercase text-[8px] tracking-wide">Pembayaran:</span>
                            <span className={cn(
                              "px-2 py-0.5 rounded-md font-black uppercase text-[8px] tracking-widest",
                              booking.paymentStatus === 'Lunas' ? "bg-emerald-50 text-emerald-600 border border-emerald-100" :
                              booking.paymentStatus === 'Menunggu Verifikasi' ? "bg-amber-50 text-amber-600 border border-amber-100" :
                              "bg-rose-50 text-rose-600 border border-rose-100"
                            )}>
                              {booking.paymentStatus || 'Belum Bayar'}
                            </span>
                            <span className="text-slate-500 font-bold">
                              ({booking.paymentMethod || 'QRIS'})
                            </span>
                          </div>

                          {booking.paymentProofUrl && (
                            <button
                              onClick={() => {
                                const w = window.open();
                                if (w) w.document.write(`<img src="${booking.paymentProofUrl}" style="max-width: 100%; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);" />`);
                              }}
                              className="text-[8px] font-black uppercase tracking-wider text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-2 shadow-sm border border-emerald-150 py-0.5 rounded cursor-pointer transition-colors"
                            >
                              Lihat Bukti Transfer
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="bg-slate-50/50 p-4 md:w-56 flex flex-row md:flex-col justify-center gap-3 border-t md:border-t-0 md:border-l border-slate-100">
                      <button 
                        onClick={() => onChat(booking.id)}
                        className="flex-1 py-3 bg-white text-slate-900 rounded-xl border border-slate-200 hover:border-slate-900 transition-all text-[9px] font-black uppercase tracking-widest"
                      >
                        Chat
                      </button>
                      
                      {booking.status === 'Menunggu' && (
                        <button 
                          onClick={() => onUpdateStatus(booking.id, 'Proses')}
                          className="flex-1 py-3 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-all shadow-sm text-[9px] font-black uppercase tracking-widest"
                        >
                          Mulai
                        </button>
                      )}
                      
                      {booking.status === 'Proses' && (
                        <button 
                          onClick={() => onUpdateStatus(booking.id, 'Selesai')}
                          className="flex-1 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all shadow-sm text-[9px] font-black uppercase tracking-widest"
                        >
                          Selesai
                        </button>
                      )}

                      {booking.status === 'Selesai' && (
                        <div className="flex-1 flex items-center justify-center gap-1.5 py-3 px-4 bg-emerald-50 rounded-xl border border-emerald-100">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                          <span className="text-emerald-500 font-black text-[8px] uppercase tracking-widest">Logged</span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </div>
        </div>
      </main>
  </div>
);
};
